// src/constants/roles.js
// ===== ROLE DEFINITIONS =====
import { PERMISSIONS, expandPermissions } from "./permissions.js";

// ===== ROLE CONFIGURATIONS =====
export const ROLES = {
  // ===== SUPER ADMIN ROLE =====
  SUPER_ADMIN: {
    name: "Super Administrator",
    description: "Complete system access with all permissions",
    level: 5,
    color: "#dc3545", // Bootstrap danger color
    icon: "fas fa-crown",
    permissions: Object.values(PERMISSIONS), // All permissions
    features: {
      canImpersonate: true,
      canAccessDevTools: true,
      canModifySystemSettings: true,
      canViewAuditLogs: true,
      bypassRateLimit: true,
    },
    restrictions: {
      maxUsers: null, // No limit
      canBeDeleted: false,
      requiresMFA: true,
    },
  },

  // ===== ADMIN ROLE =====
  ADMIN: {
    name: "Administrator",
    description: "Full administrative access to users and system features",
    level: 90,
    color: "#fd7e14", // Bootstrap warning color
    icon: "fas fa-user-shield",
    permissions: [
      // User Management
      PERMISSIONS.USER_VIEW,
      PERMISSIONS.USER_CREATE,
      PERMISSIONS.USER_EDIT,
      PERMISSIONS.USER_DELETE,
      PERMISSIONS.USER_BULK,
      PERMISSIONS.USER_EXPORT,

      // Dashboard
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.DASHBOARD_ANALYTICS,
      PERMISSIONS.DASHBOARD_EXPORT,

      // Reports
      PERMISSIONS.REPORT_VIEW,
      PERMISSIONS.REPORT_CREATE,
      PERMISSIONS.REPORT_EDIT,
      PERMISSIONS.REPORT_DELETE,
      PERMISSIONS.REPORT_EXPORT,
      PERMISSIONS.REPORT_SCHEDULE,

      // System
      PERMISSIONS.SYSTEM_MONITOR,
      PERMISSIONS.SETTING_VIEW,
      PERMISSIONS.SETTING_EDIT,

      // Files
      PERMISSIONS.FILE_VIEW,
      PERMISSIONS.FILE_UPLOAD,
      PERMISSIONS.FILE_DOWNLOAD,
      PERMISSIONS.FILE_DELETE,

      // Notifications
      PERMISSIONS.NOTIFICATION_VIEW,
      PERMISSIONS.NOTIFICATION_CREATE,
      PERMISSIONS.NOTIFICATION_UPDATE,
      PERMISSIONS.NOTIFICATION_DELETE,

      // Roles (can manage roles below their level)
      PERMISSIONS.ROLE_VIEW,
      PERMISSIONS.ROLE_ASSIGN,

      // Audit
      PERMISSIONS.AUDIT_VIEW,

      // Auth
      PERMISSIONS.AUTH_PROFILE,
    ],
    features: {
      canImpersonate: false,
      canAccessDevTools: true,
      canModifySystemSettings: true,
      canViewAuditLogs: true,
      bypassRateLimit: false,
    },
    restrictions: {
      maxUsers: 1000,
      canBeDeleted: true,
      requiresMFA: true,
    },
  },

  // ===== MANAGER ROLE =====
  MANAGER: {
    name: "Manager",
    description:
      "Management access with user oversight and reporting capabilities",
    level: 70,
    color: "#198754", // Bootstrap success color
    icon: "fas fa-users-cog",
    permissions: [
      // User Management (limited)
      PERMISSIONS.USER_VIEW,
      PERMISSIONS.USER_EDIT, // Can edit but not create/delete
      PERMISSIONS.USER_EXPORT,

      // Dashboard
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.DASHBOARD_ANALYTICS,

      // Reports
      PERMISSIONS.REPORT_VIEW,
      PERMISSIONS.REPORT_CREATE,
      PERMISSIONS.REPORT_EXPORT,

      // Files
      PERMISSIONS.FILE_VIEW,
      PERMISSIONS.FILE_UPLOAD,
      PERMISSIONS.FILE_DOWNLOAD,

      // Notifications
      PERMISSIONS.NOTIFICATION_VIEW,
      PERMISSIONS.NOTIFICATION_UPDATE,

      // Auth
      PERMISSIONS.AUTH_PROFILE,
    ],
    features: {
      canImpersonate: false,
      canAccessDevTools: false,
      canModifySystemSettings: false,
      canViewAuditLogs: false,
      bypassRateLimit: false,
    },
    restrictions: {
      maxUsers: 100,
      canBeDeleted: true,
      requiresMFA: false,
    },
  },

  // ===== TEAM_LEAD ROLE =====
  TEAM_LEAD: {
    name: "Team Lead",
    description: "Team leadership with limited user management and reporting",
    level: 60,
    color: "#0d6efd", // Bootstrap primary color
    icon: "fas fa-user-tie",
    permissions: [
      // User Management (very limited)
      PERMISSIONS.USER_VIEW,

      // Dashboard
      PERMISSIONS.DASHBOARD_VIEW,

      // Reports (basic)
      PERMISSIONS.REPORT_VIEW,
      PERMISSIONS.REPORT_CREATE,

      // Files
      PERMISSIONS.FILE_VIEW,
      PERMISSIONS.FILE_UPLOAD,
      PERMISSIONS.FILE_DOWNLOAD,

      // Notifications
      PERMISSIONS.NOTIFICATION_VIEW,
      PERMISSIONS.NOTIFICATION_UPDATE,

      // Auth
      PERMISSIONS.AUTH_PROFILE,
    ],
    features: {
      canImpersonate: false,
      canAccessDevTools: false,
      canModifySystemSettings: false,
      canViewAuditLogs: false,
      bypassRateLimit: false,
    },
    restrictions: {
      maxUsers: 50,
      canBeDeleted: true,
      requiresMFA: false,
    },
  },

  // ===== USER ROLE =====
  USER: {
    name: "User",
    description: "Standard user access with basic functionality",
    level: 30,
    color: "#6c757d", // Bootstrap secondary color
    icon: "fas fa-user",
    permissions: [
      // Basic access
      PERMISSIONS.USER_VIEW, // Can view other users (team members)

      // Dashboard (basic)
      PERMISSIONS.DASHBOARD_VIEW,

      // Files (basic)
      PERMISSIONS.FILE_VIEW,
      PERMISSIONS.FILE_UPLOAD,
      PERMISSIONS.FILE_DOWNLOAD,

      // Notifications
      PERMISSIONS.NOTIFICATION_VIEW,
      PERMISSIONS.NOTIFICATION_UPDATE,

      // Auth
      PERMISSIONS.AUTH_PROFILE,
    ],
    features: {
      canImpersonate: false,
      canAccessDevTools: false,
      canModifySystemSettings: false,
      canViewAuditLogs: false,
      bypassRateLimit: false,
    },
    restrictions: {
      maxUsers: 10,
      canBeDeleted: true,
      requiresMFA: false,
    },
  },

  // ===== VIEWER ROLE =====
  VIEWER: {
    name: "Viewer",
    description: "Read-only access to basic information",
    level: 20,
    color: "#20c997", // Bootstrap info color
    icon: "fas fa-eye",
    permissions: [
      // View only
      PERMISSIONS.USER_VIEW,
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.REPORT_VIEW,
      PERMISSIONS.FILE_VIEW,
      PERMISSIONS.NOTIFICATION_VIEW,
      PERMISSIONS.AUTH_PROFILE,
    ],
    features: {
      canImpersonate: false,
      canAccessDevTools: false,
      canModifySystemSettings: false,
      canViewAuditLogs: false,
      bypassRateLimit: false,
    },
    restrictions: {
      maxUsers: 5,
      canBeDeleted: true,
      requiresMFA: false,
    },
  },

  // ===== GUEST ROLE =====
  GUEST: {
    name: "Guest",
    description: "Temporary access with minimal permissions",
    level: 10,
    color: "#6f42c1", // Bootstrap purple
    icon: "fas fa-user-clock",
    permissions: [
      // Minimal access
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.AUTH_PROFILE,
    ],
    features: {
      canImpersonate: false,
      canAccessDevTools: false,
      canModifySystemSettings: false,
      canViewAuditLogs: false,
      bypassRateLimit: false,
    },
    restrictions: {
      maxUsers: 1,
      canBeDeleted: true,
      requiresMFA: false,
      expiresAfter: 7 * 24 * 60 * 60 * 1000, // 7 days
    },
  },

  // ===== SYSTEM ROLE =====
  SYSTEM: {
    name: "System",
    description: "Internal system processes and automated tasks",
    level: 999,
    color: "#343a40", // Bootstrap dark
    icon: "fas fa-robot",
    permissions: Object.values(PERMISSIONS), // All permissions for system processes
    features: {
      canImpersonate: true,
      canAccessDevTools: false,
      canModifySystemSettings: true,
      canViewAuditLogs: true,
      bypassRateLimit: true,
    },
    restrictions: {
      maxUsers: null,
      canBeDeleted: false,
      requiresMFA: false,
      isSystemRole: true,
    },
  },
};

// ===== ROLE UTILITIES =====

/**
 * Get role by key
 * @param {string} roleKey - The role key
 * @returns {object|null} Role object or null if not found
 */
export const getRole = (roleKey) => {
  return ROLES[roleKey] || null;
};

/**
 * Get all available roles
 * @param {boolean} includeSystem - Whether to include system roles
 * @returns {object[]} Array of role objects with keys
 */
export const getAllRoles = (includeSystem = false) => {
  return Object.entries(ROLES)
    .filter(([, role]) => includeSystem || !role.restrictions?.isSystemRole)
    .map(([key, role]) => ({ key, ...role }))
    .sort((a, b) => b.level - a.level);
};

/**
 * Check if a role can manage another role
 * @param {string} managerRole - The role doing the management
 * @param {string} targetRole - The role being managed
 * @returns {boolean} True if can manage
 */
export const canManageRole = (managerRole, targetRole) => {
  const manager = getRole(managerRole);
  const target = getRole(targetRole);

  if (!manager || !target) return false;
  if (manager.restrictions?.isSystemRole) return true;

  return manager.level > target.level;
};

/**
 * Get roles that a given role can manage
 * @param {string} roleKey - The role key
 * @returns {string[]} Array of manageable role keys
 */
export const getManageableRoles = (roleKey) => {
  const role = getRole(roleKey);
  if (!role) return [];

  return Object.entries(ROLES)
    .filter(([targetKey, targetRole]) => {
      if (targetKey === roleKey) return false;
      if (targetRole.restrictions?.isSystemRole) return false;
      return role.level > targetRole.level;
    })
    .map(([key]) => key);
};

/**
 * Get expanded permissions for a role (including dependencies)
 * @param {string} roleKey - The role key
 * @returns {string[]} Array of permissions including dependencies
 */
export const getRoleExpandedPermissions = (roleKey) => {
  const role = getRole(roleKey);
  if (!role) return [];

  return expandPermissions(role.permissions);
};

/**
 * Check if a role has a specific permission
 * @param {string} roleKey - The role key
 * @param {string} permission - The permission to check
 * @returns {boolean} True if role has permission
 */
export const roleHasPermission = (roleKey, permission) => {
  const expandedPermissions = getRoleExpandedPermissions(roleKey);
  return expandedPermissions.includes(permission);
};

/**
 * Get the highest level role from an array of roles
 * @param {string[]} roleKeys - Array of role keys
 * @returns {string|null} The highest level role key
 */
export const getHighestRole = (roleKeys) => {
  if (!roleKeys.length) return null;

  return roleKeys.reduce((highest, current) => {
    const currentRole = getRole(current);
    const highestRole = getRole(highest);

    if (!currentRole) return highest;
    if (!highestRole) return current;

    return currentRole.level > highestRole.level ? current : highest;
  });
};

/**
 * Check if role requires MFA
 * @param {string} roleKey - The role key
 * @returns {boolean} True if MFA is required
 */
export const roleRequiresMFA = (roleKey) => {
  const role = getRole(roleKey);
  return role?.restrictions?.requiresMFA || false;
};

/**
 * Get role display information
 * @param {string} roleKey - The role key
 * @returns {object} Display information for the role
 */
export const getRoleDisplayInfo = (roleKey) => {
  const role = getRole(roleKey);
  if (!role) {
    return {
      name: "Unknown Role",
      color: "#6c757d",
      icon: "fas fa-question-circle",
      badgeClass: "bg-secondary",
    };
  }

  return {
    name: role.name,
    color: role.color,
    icon: role.icon,
    badgeClass: `bg-${role.color.replace("#", "")}`, // Convert hex to bootstrap class
    level: role.level,
  };
};

/**
 * Check if a role is deletable
 * @param {string} roleKey - The role key
 * @returns {boolean} True if role can be deleted
 */
export const isRoleDeletable = (roleKey) => {
  const role = getRole(roleKey);
  return role?.restrictions?.canBeDeleted !== false;
};

/**
 * Get roles available for assignment by a given role
 * @param {string} assignerRole - The role doing the assignment
 * @returns {string[]} Array of assignable role keys
 */
export const getAssignableRoles = (assignerRole) => {
  const assigner = getRole(assignerRole);
  if (!assigner?.permissions.includes(PERMISSIONS.ROLE_ASSIGN)) {
    return [];
  }

  return getManageableRoles(assignerRole);
};

/**
 * Validate role configuration
 * @param {object} roleConfig - The role configuration to validate
 * @returns {object} Validation result with isValid and errors
 */
export const validateRoleConfig = (roleConfig) => {
  const errors = [];

  if (!roleConfig.name) {
    errors.push("Role name is required");
  }

  if (!roleConfig.description) {
    errors.push("Role description is required");
  }

  if (typeof roleConfig.level !== "number") {
    errors.push("Role level must be a number");
  }

  if (!Array.isArray(roleConfig.permissions)) {
    errors.push("Role permissions must be an array");
  }

  // Check for invalid permissions
  const invalidPermissions = roleConfig.permissions?.filter(
    (permission) => !Object.values(PERMISSIONS).includes(permission)
  );

  if (invalidPermissions?.length > 0) {
    errors.push(`Invalid permissions: ${invalidPermissions.join(", ")}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// ===== ROLE CONSTANTS FOR EASY IMPORT =====
export const ROLE_KEYS = Object.keys(ROLES);
export const ROLE_LEVELS = Object.fromEntries(
  Object.entries(ROLES).map(([key, role]) => [key, role.level])
);

// Default role for new users
export const DEFAULT_ROLE = "USER";

// Roles that can access admin features
export const ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN"];

// Roles that can manage users
export const USER_MANAGER_ROLES = ["SUPER_ADMIN", "ADMIN", "MANAGER"];

// Export as default
export default ROLES;
